import React from "react";
import {DatePicker, Form, Input, InputNumber, Select, Space, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {entityTypeFields} from './entityAssetFormPayload';

const {Text} = Typography;

export default function EntityAssetFormFields({entityType}) {
    const {t} = useTranslation();

    if (!entityType) return null;

    const renderedFieldIds = new Set();
    const renderField = field => {
        if (!field || renderedFieldIds.has(field.id)) return null;
        renderedFieldIds.add(field.id);

        return (
            <Form.Item
                key={field.id}
                name={["custom_meta", field.id.toString()]}
                label={field.name}
                extra={field.description}
                rules={field.required ? [{required: true}] : []}
            >
                {field.free ? (
                    freeFieldInput(field)
                ) : (
                    <Select
                        mode={field.multi ? "multiple" : undefined}
                        allowClear
                        options={(field.custom_meta_values_attributes || []).map(value => ({
                            value: value.id,
                            label: value.text || value.date || value.number
                        }))}
                    />
                )}
            </Form.Item>
        );
    };

    if (entityType.schema_version >= 2) {
        const fieldsById = new Map(entityTypeFields(entityType).map(field => [field.id, field]));
        const titleBinding = entityType.display_config?.display_title_source;
        return <Space direction="vertical" style={{width: '100%'}}>
            {(!titleBinding || titleBinding === 'input') && <Form.Item name="display_title" label="Display Title" rules={[{required: true, whitespace: true}]}><Input/></Form.Item>}
            {(entityType.fields || []).map(field => field.custom_meta_field_id ? renderField(fieldsById.get(field.custom_meta_field_id)) :
                <Form.Item key={field.key} name={['standard_meta', field.standard_field_key]} label={field.name} rules={field.required ? [{required: true}] : []}>{freeFieldInput(field)}</Form.Item>)}
        </Space>;
    }

    const groups = (entityType.custom_meta_groups || []).map(groupMembership => {
        const group = groupMembership.custom_meta_group;
        const fields = group?.custom_meta_fields?.map(fieldMembership => renderField(fieldMembership.custom_meta_field)).filter(Boolean);
        if (!fields?.length) return null;

        return (
            <Space direction="vertical" style={{width: "100%"}} key={group.id}>
                <Text strong>{group.name}</Text>
                {fields}
            </Space>
        );
    }).filter(Boolean);

    const directFields = (entityType.custom_meta_fields || [])
        .map(fieldMembership => renderField(fieldMembership.custom_meta_field))
        .filter(Boolean);

    return (
        <Space direction="vertical" style={{width: "100%"}} size="middle">
            {groups}
            {!!directFields.length && (
                <Space direction="vertical" style={{width: "100%"}}>
                    <Text strong>{t("ungrouped-fields", "Ungrouped Fields")}</Text>
                    {directFields}
                </Space>
            )}
        </Space>
    );
}

function freeFieldInput(field) {
    if (field.multi) {
        return (
            <Select
                mode="tags"
                tokenSeparators={[",", ";"]}
                placeholder={field.value_type === "asset_guid" ? "MG GUID" : undefined}
            />
        );
    }

    switch (field.value_type) {
        case "date":
            return <DatePicker style={{width: "100%"}}/>;
        case "datetime":
            return <DatePicker showTime style={{width: "100%"}}/>;
        case "number":
            return <InputNumber style={{width: "100%"}}/>;
        case "price":
            return <InputNumber min={0} precision={4} prefix="$" style={{width: "100%"}}/>;
        case "asset_guid":
            return <Input placeholder="MG GUID"/>;
        case "text":
            return <Input.TextArea rows={2}/>;
        default:
            return <Input/>;
    }
}
